import { Link } from "react-router-dom";

/**
 * Shared empty-state surface used across the app.
 *
 * Design: one visual language for "there is nothing here (yet)" so
 * every page — public and admin — feels like the same product.
 *
 * Every user-facing empty view (Saved, Opportunities zero-results,
 * Knowledge zero-results, Review queue empty, Audit log empty) should
 * consume this component instead of hand-rolling markup.
 *
 * Props:
 *   icon          — lucide-react Icon component (required)
 *   title         — Arabic H3 line
 *   description   — small helper line
 *   ctaLabel      — optional CTA text
 *   ctaHref       — internal route for the CTA (Link)
 *   ctaOnClick    — button variant when there is no destination
 *   testId        — data-testid on the outer container
 *   tone          — "quiet" (default, dashed) | "solid" (filled bg)
 */
export const EmptyState = ({
  icon: Icon,
  title,
  description,
  ctaLabel,
  ctaHref,
  ctaOnClick,
  testId,
  tone = "quiet",
}) => {
  const shellCls =
    tone === "solid"
      ? "bg-surface-50 border border-surface-200"
      : "bg-white border border-dashed border-surface-200";

  return (
    <div
      data-testid={testId}
      className={`flex flex-col items-center justify-center gap-3 rounded-2xl px-6 py-16 text-center ${shellCls}`}
    >
      {Icon && (
        <span
          aria-hidden
          className="mb-1 inline-flex h-11 w-11 items-center justify-center rounded-full bg-surface-50 text-ink-500 ring-1 ring-surface-200"
        >
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </span>
      )}
      {title && (
        <h3 className="text-base font-semibold text-ink-900">{title}</h3>
      )}
      {description && (
        <p className="max-w-md text-[12.5px] leading-relaxed text-ink-500">
          {description}
        </p>
      )}
      {ctaLabel && ctaHref && (
        <Link
          to={ctaHref}
          data-testid={testId ? `${testId}-cta` : undefined}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-intel-800 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-intel-700"
        >
          {ctaLabel}
        </Link>
      )}
      {ctaLabel && !ctaHref && ctaOnClick && (
        <button
          type="button"
          onClick={ctaOnClick}
          data-testid={testId ? `${testId}-cta` : undefined}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-intel-800 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-intel-700"
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
};

export default EmptyState;
